import chartTickerService from '../../../../core/services/Market/Watchlist/ChartTickerService';
import priceDataService from '../../../../core/services/Prices/PriceData/PriceDataService';
import timeAndSaleService from '../../../../core/services/Prices/TimeAndSale/TimeAndSaleService';
import reducerRegistry from './../../../helpers/ReducerRegistry';
import liquidityService from './../../../../core/services/Market/MarketInDepth/LiquidityService';
import indexService from './../../../../core/services/Market/MarketInDepth/IndexService';
import Const from './Const';
import { executeRealtimeData } from '../../../helpers/Chart';
import registerCancelTokenAxios from './../../../helpers/RegisterCancelTokenAxios';
import ConstCommon from './../../../common/Const';
import currentState from './../../../helpers/CurrentState';
import { formatData } from '../../../helpers/Api';

const REDUCER_NAME = 'MARKET_IN_DEPTH:LIQUIDITY:';
const FETCH_LIST_LIQUIDITY = `${REDUCER_NAME}FETCH_LIST_LIQUIDITY`;
const CHANGE_SIZE_COMPONENT = `${REDUCER_NAME}CHANGE_SIZE_COMPONENT`;
const SUBSCRIBE_REALTIME_DATA = `${REDUCER_NAME}SUBSCRIBE_REALTIME_DATA`;
const CHANGE_COM_GROUP_CODE = `${REDUCER_NAME}CHANGE_COM_GROUP_CODE`;
const CHANGE_TIME_RANGE = `${REDUCER_NAME}CHANGE_TIME_RANGE`;
const SET_LOADING = `${REDUCER_NAME}SET_LOADING`;
const INIT_COMPONENT = `${REDUCER_NAME}INIT_COMPONENT`;
const UNMOUNT_COMPONENT = `${REDUCER_NAME}UNMOUNT_COMPONENT`;
const CHANGE_CODE = `${REDUCER_NAME}CHANGE_CODE`;
const CHANGE_PRICE_INFO = `${REDUCER_NAME}CHANGE_PRICE_INFO`;
const FETCH_BUSD_SUCCESS = `${REDUCER_NAME}FETCH_BUSD_SUCCESS`;

export {
  REDUCER_NAME,
  changeTimeRange,
  subscribeRealtimeData,
  fetchListIndex,
  fetchListLiquidity,
  setLoading,
  changeCode,
  fetchPriceInfo,
  fetchLiquidity,
  fetchBusd,
};

function fetchListLiquidity(comGroupCode, timeRange, resolve) {
  registerCancelTokenAxios.delete(REDUCER_NAME + 'fetchListLiquidity');
  const source = registerCancelTokenAxios.register(
    REDUCER_NAME + 'fetchListLiquidity',
  );
  liquidityService
    .getLiquiditySeries(
      {
        ComGroupCode: comGroupCode,
        TimeRange: timeRange,
      },
      source,
    )
    .then(response => {
      resolve({ listLiquidity: formatData(response) });
    });
}

function fetchPriceInfo() {
  return function(dispatch, getState) {
    const currentSearch = getState()[REDUCER_NAME].currentSearch;

    priceDataService
      .getLatestPrice({ Code: currentSearch.code })
      .then(response => {
        const data =
          response.items && response.items[0] ? response.items[0] : {};
        dispatch({
          type: CHANGE_PRICE_INFO,
          payload: data,
        });
      });
  };
}

function changeCode(currentSearch) {
  return dispatch => {
    dispatch({
      type: CHANGE_CODE,
      payload: {
        currentSearch,
      },
    });
    const { code, displayCode, isTicker } = currentSearch;

    currentState.add(
      ConstCommon.listComponent.MarketInDepth,
      'liquidity.currentSearch',
      { code, displayCode, isTicker },
    );
  };
}

function fetchListIndex(comGroupCode, resolve) {
  registerCancelTokenAxios.delete(REDUCER_NAME + 'fetchListIndex');
  const source = registerCancelTokenAxios.register(
    REDUCER_NAME + 'fetchListIndex',
  );
  indexService
    .getIndexSeries(
      {
        ComGroupCode: comGroupCode,
        TimeRange: Const.timeRange.ONE_DAY,
      },
      source,
    )
    .then(response => {
      resolve({ listIndex: formatData(response) });
    });
}

function fetchTickerSeries(organCode, resolve) {
  registerCancelTokenAxios.delete(REDUCER_NAME + 'fetchListIndex');
  const source = registerCancelTokenAxios.register(
    REDUCER_NAME + 'fetchListIndex',
  );
  chartTickerService
    .getTickerSeries(
      {
        OrganCode: organCode,
        TimeRange: Const.timeRange.ONE_DAY,
      },
      source,
    )
    .then(response => {
      resolve({ listIndex: formatData(response) });
    });
}

function changeTimeRange(timeRange) {
  return (dispatch, getState) => {
    const currentSearch = getState()[REDUCER_NAME].currentSearch;

    dispatch(setLoading(true));
    dispatch({
      type: CHANGE_TIME_RANGE,
      payload: {
        timeRange,
      },
    });
    currentState.add(
      ConstCommon.listComponent.MarketInDepth,
      'liquidity.timeRange',
      timeRange,
    );

    const promises = [];

    const promise = new Promise(resolve => {
      fetchListLiquidity(currentSearch.code, timeRange, resolve);
    });
    promises.push(promise);
    Promise.all(promises).then(res => {
      dispatchFetchListLiquidity(dispatch, res);

      dispatch(setLoading(false));
    });
  };
}

function getBusdPromise(currentSearch) {
  if (currentSearch.isTicker) {
    return timeAndSaleService
      .getBuSdChart({
        Code: currentSearch.code,
      })
      .then(res => ({ busd: res.items || [] }));
  }

  return liquidityService
    .getBuSd({ ComGroupCode: currentSearch.code })
    .then(res => ({ busd: res.items || [] }));
}

function fetchBusd() {
  return function(dispatch, getState) {
    const currentSearch = getState()[REDUCER_NAME].currentSearch;
    getBusdPromise(currentSearch).then(res => {
      dispatch({
        type: FETCH_BUSD_SUCCESS,
        payload: res.busd,
      });
    });
  };
}

function fetchLiquidity() {
  return function(dispatch, getState) {
    dispatch(setLoading(true));
    const timeRange = getState()[REDUCER_NAME].timeRange;
    const currentSearch = getState()[REDUCER_NAME].currentSearch;
    const promises = [];
    promises.push(getBusdPromise(currentSearch));
    const promiseLiquidity = new Promise((resolve, reject) => {
      fetchListLiquidity(currentSearch.code, timeRange, resolve);
    });
    promises.push(promiseLiquidity);
    if (currentSearch.isTicker) {
      promises.push(
        new Promise((resolve, reject) => {
          fetchTickerSeries(currentSearch.code, resolve);
        }),
      );
    } else {
      promises.push(
        new Promise((resolve, reject) => {
          fetchListIndex(currentSearch.code, resolve);
        }),
      );
    }

    Promise.all(promises).then(res => {
      dispatchFetchListLiquidity(dispatch, res, currentSearch);

      dispatch(setLoading(false));
    });
  };
}

function subscribeRealtimeData(data) {
  return {
    type: SUBSCRIBE_REALTIME_DATA,
    payload: {
      data,
    },
  };
}

function setLoading(isLoading = false) {
  return {
    type: SET_LOADING,
    payload: {
      isLoading,
    },
  };
}

const initialState = {
  listIndex: [],
  listLiquidity: [],
  busd: [],
  component: {},
  priceInfo: {
    priceInfo: {},
    bidAskInfo: null,
  },
  isLoading: false,
  currentSearch: {
    code: Const.defaultComGroupCode,
    displayCode: Const.defaultComGroupCode,
  },
};

function reducer(state = {}, action) {
  switch (action.type) {
    case INIT_COMPONENT:
      return {
        ...state,
        ...initialState,
        currentSearch:
          currentState.get(
            ConstCommon.listComponent.MarketInDepth,
            'liquidity.currentSearch',
          ) || initialState.currentSearch,
        timeRange:
          currentState.get(
            ConstCommon.listComponent.MarketInDepth,
            'liquidity.timeRange',
          ) || Const.timeRange.ONE_DAY,
      };

    case UNMOUNT_COMPONENT:
      return {};
    case CHANGE_CODE:
      const { currentSearch } = action.payload;

      return {
        ...state,
        currentSearch,
      };

    case SET_LOADING:
    case CHANGE_COM_GROUP_CODE:
    case CHANGE_TIME_RANGE:
    case FETCH_LIST_LIQUIDITY:
      return {
        ...state,
        ...action.payload,
      };

    case CHANGE_SIZE_COMPONENT:
      return {
        ...state,
        component: action.payload,
      };

    case SUBSCRIBE_REALTIME_DATA:
      const { data } = action.payload;
      return {
        ...state,
        listIndex: executeRealtimeData(data, [...state.listIndex]),
      };
    case CHANGE_PRICE_INFO:
      return {
        ...state,
        priceInfo: action.payload,
      };
    case FETCH_BUSD_SUCCESS:
      return {
        ...state,
        busd: action.payload || [],
      };
    default:
      return state;
  }
}

reducerRegistry.register(REDUCER_NAME, reducer);

function dispatchFetchListLiquidity(dispatch, res) {
  let payload = {};

  res.forEach(val => {
    payload = { ...payload, ...val };
  });

  dispatch({
    type: FETCH_LIST_LIQUIDITY,
    payload,
  });
}
